/**
 * CalendarsTableRow - Linha da tabela de calendários
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Box,
  Typography,
  Avatar,
  Checkbox,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import CollectionActions from './CollectionActions';

const CalendarsTableRow = memo(({
  row,
  onRefresh,
  onNavigate,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const isDark = tokens?.isDark ?? true;

  // Cor do avatar - USA TOKENS DINÂMICOS
  const avatarColor = tokens?.semantic?.success;

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 0.5, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 0.5, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            border: `1px solid ${avatarColor}40`,
          }}
        >
          <EventIcon sx={{ fontSize: 18, color: avatarColor }} />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 0.5, width: 70 }}>
        <Typography variant="caption" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 0.5 }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 300 }}>
          {row.name}
        </Typography>
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 0.5 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/calendar"
          endpoint="calendars"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

CalendarsTableRow.displayName = 'CalendarsTableRow';

export default CalendarsTableRow;
